/* eslint-disable max-len */
import React from 'react';
import { connect } from 'react-redux';
import { v4 } from '@leverege/base62-util';
import { GlobalState, Selection } from '@leverege/ui-redux';
import { DataSourceFactory } from '@leverege/ui-attributes';
import { ValuesCache } from '@leverege/value-cache';
import { Flex, DateTimeRangePicker } from '@leverege/ui-elements';
import { Toolbar } from '@leverege/ui-plugin';
import { DashboardContextConsumer } from '@leverege/ui-dashboard';
import HistoryPaginator from "./HistoryPaginator.js";
import Model from "./models/HistoryViewModel.js";
import S from './HistoryView.css';
import ResponsiveView from "../responsive/ResponsiveView.js";
import { HeaderToolbar } from "../Util.js";
import DataViewerCardModel from "../dashboard/DataViewerCardModel.js";
import { getQueryTrigger } from "./Util.js";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const queryModeAll = {
  all: true,
  selected: false,
  targeted: false
};
class HistoryView extends React.Component {
  constructor(props) {
    super(props);
    this.queryId = v4();
    this.cache = new ValuesCache();
    this.paginator = new HistoryPaginator({
      onPageChange: this.onSetPage
    });
    const {
      relationship,
      dataViewer,
      delegate,
      dataViewerInstanceId
    } = props;
    this.trigger = getQueryTrigger({
      objectType: relationship.objectType,
      path: relationship.path,
      client: 'HistoryView',
      dataViewerType: dataViewer.type,
      relationship,
      delegate,
      queryName: dataViewerInstanceId?.current,
      caches: this.cache,
      loadData: this.loadData,
      shouldReloadData: this.shouldReloadData
    }, this.getContext());
    this.state = {
      page: 0,
      loadingPages: false
    };
  }
  componentDidMount() {
    this.trigger?.init?.();
  }
  componentWillUnmount() {
    this.unmounted = true;
    this.trigger?.cleanup?.();
  }
  getItemsToQuery(queryModes, selected, targeted) {
    return this.cache.calc('itemsToQuery', () => {
      const items = new Set();
      if (queryModes.selected) {
        const selIds = Selection.getIds(selected);
        selIds.forEach(id => {
          items.add(id);
        });
      }
      if (queryModes.targeted) {
        const tgtIds = Selection.getIds(targeted);
        tgtIds.forEach(id => {
          items.add(id);
        });
      }
      return Array.from(items);
    }, queryModes, selected, targeted);
  }
  componentDidUpdate(prevProps, prevState) {
    const {
      item,
      settingsPath,
      dashboardContext,
      dataViewerCardModel
    } = this.props;
    const {
      model: pModel,
      item: pItem,
      settingsPath: pSettingsPath,
      dashboardContext: pDashboardContext,
      dataViewerCardModel: pDataViewerCardModel
    } = prevProps;
    const {
      page: pPage
    } = prevState;
    const pPerPage = Model.getPerPage(pModel);
    const pQueryModes = item ? queryModeAll : Model.getQueryModes(pModel);
    const pNeedsDate = pDataViewerCardModel && DataViewerCardModel.getNeedsDate(pDataViewerCardModel);
    const pNeedsCondition = pDataViewerCardModel && DataViewerCardModel.getNeedsCondition(pDataViewerCardModel);
    const pNeedsOrder = pDataViewerCardModel && DataViewerCardModel.getNeedsOrder(pDataViewerCardModel);
    const pRange = pNeedsDate ? pDashboardContext?.options?.dateTimeRange : Model.getRange(pModel);
    const pCondition = pNeedsCondition ? pDashboardContext?.options?.condition : Model.getCondition(pModel);
    const pOrder = pNeedsOrder ? pDashboardContext?.options?.order : Model.getOrder(pModel);
    const {
      model
    } = this.props;
    const {
      page
    } = this.state;
    const perPage = Model.getPerPage(model);
    const queryModes = item ? queryModeAll : Model.getQueryModes(model);
    const needsDate = dataViewerCardModel && DataViewerCardModel.getNeedsDate(dataViewerCardModel);
    const needsCondition = dataViewerCardModel && DataViewerCardModel.getNeedsCondition(dataViewerCardModel);
    const needsOrder = dataViewerCardModel && DataViewerCardModel.getNeedsOrder(dataViewerCardModel);
    const range = needsDate ? dashboardContext?.options?.dateTimeRange : Model.getRange(model);
    const condition = needsCondition ? dashboardContext?.options?.condition : Model.getCondition(model);
    const order = needsOrder ? dashboardContext?.options?.order : Model.getOrder(model);
    let load = false;
    if (range !== pRange) {
      load = true;
    }
    if (condition !== pCondition) {
      load = true;
    }
    if (order !== pOrder) {
      load = true;
    }
    if (queryModes.selected) {
      const {
        selected: pSelected
      } = prevProps;
      const {
        selected
      } = this.props;
      if (selected && pSelected !== selected) {
        load = true;
      }
    }
    if (queryModes.targeted) {
      const {
        targeted: pTargeted
      } = prevProps;
      const {
        targeted
      } = this.props;
      if (targeted && pTargeted !== targeted) {
        load = true;
      }
    }
    if (pQueryModes !== queryModes) {
      load = true;
    }
    if (pPage !== page || pPerPage !== perPage) {
      load = true;
    }
    if (pItem == null && item != null) {
      load = true;
    }
    if (settingsPath !== pSettingsPath) {
      load = true;
    }
    if (load) {
      this.loadData();
    }
  }
  getDeviceWhere() {
    const {
      selected,
      targeted,
      item,
      model
    } = this.props;
    const queryModes = item ? queryModeAll : Model.getQueryModes(model);
    if (item) {
      return ['$deviceId', '=', item.id];
    }
    return ['$deviceId', 'IN', this.getItemsToQuery(queryModes, selected, targeted)];
  }
  onSetPage = ({
    page,
    perPage
  }) => {
    const {
      settings,
      onSettingsChange,
      settingsData
    } = this.props;
    this.setState({
      page
    });
    onSettingsChange({
      value: Model.setPerPage(settings, perPage),
      data: settingsData
    });
    // UserSettings.set( settingsPath, Model.setPerPage( model, perPage ) )
  };
  getRange() {
    const {
      model,
      dashboardContext,
      dataViewerCardModel
    } = this.props;
    const {
      options
    } = dashboardContext;
    const needsDate = dataViewerCardModel && DataViewerCardModel.getNeedsDate(dataViewerCardModel);
    let range;
    if (!needsDate) {
      range = Model.getRange(model);
    }
    if ((range == null || needsDate) && options) {
      range = options.dateTimeRange;
    }
    range = DateTimeRangePicker.getRange(range || Model.getRange(model));
    return range;
  }
  getOrder() {
    const {
      model,
      delegate,
      dashboardContext,
      dataViewerCardModel
    } = this.props;
    const needsOrder = dataViewerCardModel && DataViewerCardModel.getNeedsOrder(dataViewerCardModel);
    const attrSort = needsOrder ? dashboardContext?.options?.order : Model.getOrder(model);
    const order = delegate.historyDelegate.convertToSort(attrSort, this.props);
    return order || null;
  }
  getWhere() {
    const {
      selected,
      targeted,
      item,
      model,
      delegate,
      dashboardContext,
      dataViewerCardModel
    } = this.props;
    const needsCondition = dataViewerCardModel && DataViewerCardModel.getNeedsCondition(dataViewerCardModel);
    const condition = needsCondition ? dashboardContext?.options?.condition : Model.getCondition(model);
    let filter;
    if (condition) {
      filter = delegate.historyDelegate.convertToFilter(condition, this.props);
    }
    const where = [];
    const queryModes = item ? queryModeAll : Model.getQueryModes(model);
    const itemsToQuery = this.getItemsToQuery(queryModes, selected, targeted);
    if (itemsToQuery.length > 0) {
      where.push(this.getDeviceWhere());
    }
    if (filter && filter.length > 0) {
      where.push(...filter);
    }
    return where.length > 0 ? where : undefined;
  }
  loadData = async () => {
    this.queryId = v4();
    const {
      model,
      selected,
      targeted,
      item
    } = this.props;
    const {
      page
    } = this.state;
    const perPage = Model.getPerPage(model);
    const queryModes = item ? queryModeAll : Model.getQueryModes(model);
    const range = this.getRange();
    const itemsToQuery = this.getItemsToQuery(queryModes, selected, targeted);
    const where = this.getWhere();
    const order = this.getOrder();
    if (!range.start) {
      return;
    }
    if (!queryModes.all && itemsToQuery.length === 0) {
      return;
    }
    const q = {
      range,
      page,
      perPage,
      where,
      order
    };
    this.trigger?.preQuery?.(q);
    try {
      await this.queryHistory(q);
      if (this.unmounted !== true) {
        this.trigger?.postQuery?.();
      }
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
      if (this.unmounted !== true) {
        this.trigger?.onQueryError?.(err);
      }
    }
  };
  shouldReloadData = () => {
    const range = this.getRange();
    return range?.end == null || new Date() <= range.end;
  };
  async queryHistory({
    range,
    page,
    perPage,
    where,
    order
  }) {
    const {
      dispatch,
      delegate,
      dataViewerInstanceId,
      model
    } = this.props;
    const getAllPages = Model.getAllPages(model);
    const currentQueryId = this.queryId;
    this.setState({
      loadingPages: true
    });
    try {
      if (getAllPages) {
        let p = 0;
        let hasMoreData = true;
        while (hasMoreData) {
          if (this.queryId !== currentQueryId) {
            break;
          }
          // eslint-disable-next-line no-await-in-loop
          const {
            items
          } = await dispatch(delegate.historyDelegate.search({
            query: {
              start: range.start,
              end: range.end,
              perPage,
              startIndex: p * perPage,
              order: order || [['$time', 'DESC']],
              where
            },
            queryName: dataViewerInstanceId.current
          }));
          p += 1;
          if (items.length < perPage) {
            hasMoreData = false;
          }
        }
      } else {
        await dispatch(delegate.historyDelegate.search({
          query: {
            start: range.start,
            end: range.end,
            perPage,
            startIndex: page * perPage,
            order: order || [['$time', 'DESC']],
            where
          },
          queryName: dataViewerInstanceId.current
        }));
      }
    } finally {
      this.setState({
        loadingPages: false
      });
    }
  }
  pushDataForward(items) {
    const accumulated = {};
    const sorted = [...items].sort((a, b) => {
      return a.time > b.time ? 1 : -1;
    });
    sorted.forEach(item => {
      const {
        deviceId,
        data
      } = item;
      const nData = {
        ...accumulated[deviceId],
        ...data.data
      };
      if (accumulated[deviceId]) {
        data.data = nData;
      }
      accumulated[item.deviceId] = nData;
    });
    return items;
  }
  createDataItems = (results, density, queryModes, selected, targeted) => {
    const {
      relationship,
      delegate,
      settings,
      loading
    } = this.props;
    const {
      page
    } = this.state;
    const {
      items
    } = results;
    const perPage = Model.getPerPage(settings);
    const type = `${relationship.blueprint.type}.history`;
    let hasNoSelection = true;
    if (queryModes.all) {
      hasNoSelection = false;
    } else if (queryModes.seleted && queryModes.rollover) {
      hasNoSelection = Selection.isEmpty(selected) && Selection.isEmpty(targeted);
    } else if (queryModes.selected) {
      hasNoSelection = Selection.isEmpty(selected);
    } else {
      hasNoSelection = Selection.isEmpty(targeted);
    }
    let historyItems = [];
    if (!hasNoSelection) {
      const dataSource = DataSourceFactory.get(type) || {
        getData: () => null
      };
      historyItems = items == null ? [] : items.map(item => {
        const ref = `${delegate.historyDelegate.getInterfaceUrl()}/${item.deviceId}.history`;
        const obj = {
          id: item.id,
          type,
          ref
        };
        const data = dataSource.getData(obj);
        obj.data = data;
        return obj;
      });
      if (density === 'dense') {
        historyItems = this.pushDataForward(historyItems);
      }
    }
    return {
      status: results?.status || null,
      loading,
      perPage,
      page,
      count: results?.total || 0,
      items: historyItems
    };
  };
  getContext(dataViewerType) {
    return {
      client: 'HistoryView',
      // : View.CLIENT,
      dataViewerType,
      clientProps: {
        ...this.props,
        exportWhere: this.getWhere()
      }
    };
  }
  rendererHeader = dataViewerModel => {
    if (dataViewerModel == null) {
      return null;
    }
    const {
      relationship: {
        path
      },
      objectType,
      inCard
    } = this.props;
    const actionBarMatchContext = {
      use: 'actionBar',
      client: 'HistoryView',
      dataViewerType: dataViewerModel.type,
      objectType,
      path
    };
    const filterBarMatchContext = {
      use: 'filterBar',
      client: 'HistoryView',
      dataViewerType: dataViewerModel.type,
      objectType,
      path
    };
    const context = this.getContext(dataViewerModel.type);
    const content = /*#__PURE__*/_jsxs(Flex, {
      className: inCard ? undefined : S.toolbars,
      variant: "tableToolbar",
      children: [/*#__PURE__*/_jsx(Toolbar, {
        className: S.toolbar,
        variant: "filterBar|actionBar",
        matchContext: filterBarMatchContext,
        context: context
      }), /*#__PURE__*/_jsx(Toolbar, {
        className: S.toolbar,
        variant: "actionBar",
        prefer: "icon",
        matchContext: actionBarMatchContext,
        context: context
      })]
    });
    return /*#__PURE__*/_jsx(HeaderToolbar, {
      inCard: inCard,
      content: content
    });
  };
  render() {
    const {
      result,
      settings,
      selected,
      targeted,
      item
    } = this.props;
    const {
      loadingPages
    } = this.state;
    const density = Model.getDensity(settings);
    const queryModes = item ? queryModeAll : Model.getQueryModes(settings);
    const dataItems = this.cache.calc('items', this.createDataItems, result, density, queryModes, selected, targeted);
    this.paginator.setData(dataItems);
    const context = this.getContext();
    return /*#__PURE__*/_jsx(ResponsiveView, {
      ...this.props,
      loadingPages: loadingPages,
      context: context,
      paginator: this.paginator,
      useContentView: true,
      contentHeader: this.rendererHeader
    });
  }
}
const withContext = Wrapped => function (props) {
  return /*#__PURE__*/_jsx(DashboardContextConsumer, {
    children: value => /*#__PURE__*/_jsx(Wrapped, {
      ...props,
      dashboardContext: value
    })
  });
};
export default connect((state, props) => {
  const {
    selectionKey,
    rolloverKey,
    targetKey,
    delegate,
    objectType: pObjectType,
    settings,
    // View,
    selected: pSelected,
    targeted: pTargeted,
    rollover: pRollover,
    item,
    dataViewerInstanceId
  } = props;
  const objectType = `${pObjectType}.history`;
  const getAll = settings ? Model.getAllPages(settings) : false;
  const queryData = delegate.historyDelegate.getList(state, dataViewerInstanceId.current, {
    getAll
  });
  const error = delegate.historyDelegate?.getError(GlobalState.get(), dataViewerInstanceId.current);
  const loading = delegate.historyDelegate?.isLoading(GlobalState.get(), dataViewerInstanceId.current);
  return {
    result: queryData || {},
    loading,
    error,
    model: settings,
    selected: pSelected || Selection.get(state, selectionKey),
    targeted: pTargeted || Selection.get(state, targetKey),
    rollover: pRollover || Selection.get(state, rolloverKey),
    objectType,
    item
  };
})(withContext(HistoryView));